import { Download, Plus, Search } from "lucide-react";
import type { ChangeEvent, JSX } from "react";
import { useTranslation } from "react-i18next";

import { Select } from "@/shared/ui/select";

import { PrimaryButton, SecondaryButton } from "./buttons";

/** Server-supported rollup status filter values (BE `status_rollup` query param). */
export type StatusFilter = "" | "active" | "finish" | "draft";

interface CampaignsToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  status: StatusFilter;
  onStatusChange: (value: StatusFilter) => void;
  period: string;
  onPeriodChange: (value: string) => void;
  periodOptions: string[];
  onExport: () => void;
  /** Opens the create-campaign modal — the view's single primary action. */
  onNewCampaign?: () => void;
}

export function CampaignsToolbar({
  search,
  onSearchChange,
  status,
  onStatusChange,
  period,
  onPeriodChange,
  periodOptions,
  onExport,
  onNewCampaign,
}: CampaignsToolbarProps): JSX.Element {
  const { t } = useTranslation("campaign");

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2.5" data-testid="campaigns-toolbar">
      <div className="relative min-w-[200px] flex-1">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-subtle)]"
        />
        <input
          type="search"
          data-testid="campaigns-search"
          value={search}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
          placeholder={t("toolbar.search_placeholder")}
          className="focus-ring h-9 w-full rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white pl-10 pr-3 text-sm text-[var(--text-primary)] shadow-[var(--sh-xs)] outline-none transition placeholder:text-[var(--text-subtle)] focus:border-[var(--brand-500)]"
        />
      </div>

      <Select
        data-testid="campaigns-status-filter"
        aria-label={t("toolbar.all_statuses")}
        value={status}
        onChange={(e) => onStatusChange(e.target.value as StatusFilter)}
      >
        <option value="">{t("toolbar.all_statuses")}</option>
        <option value="active">{t("toolbar.has_active")}</option>
        <option value="finish">{t("toolbar.has_finished")}</option>
        <option value="draft">{t("toolbar.has_draft")}</option>
      </Select>

      <Select
        data-testid="campaigns-period-filter"
        aria-label={t("toolbar.all_periods")}
        value={period}
        onChange={(e) => onPeriodChange(e.target.value)}
      >
        <option value="">{t("toolbar.all_periods")}</option>
        {periodOptions.map((p) => (
          <option key={p} value={p}>
            {p}
          </option>
        ))}
      </Select>

      <SecondaryButton data-testid="campaigns-export" onClick={onExport}>
        <Download className="h-4 w-4" aria-hidden />
        {t("toolbar.export")}
      </SecondaryButton>

      {onNewCampaign && (
        <PrimaryButton data-testid="campaign-create" onClick={onNewCampaign}>
          <Plus className="h-4 w-4" aria-hidden />
          {t("toolbar.new_campaign")}
        </PrimaryButton>
      )}
    </div>
  );
}
